import React, { useState } from "react";

// Shared shimmer skeleton, build once, use everywhere (perceived-performance.md).
export const Skeleton = ({ width = "100%", height = 16, borderRadius = 8, className = "", style = {} }) => (
  <div
    className={`skeleton ${className}`}
    style={{ width, height, borderRadius, ...style }}
    aria-hidden="true"
  />
);

// Image that holds its slot: a shimmer fills the reserved box until the image decodes,
// then the image fades in (opacity only, no slide) so grids never reflow as thumbnails
// land one by one. The parent element must reserve the shape (aspect ratio) and be
// positioned; this only swaps the inner <img> plus its placeholder.
// Cheap right-click / drag-to-save friction on media images only. Excluded on the
// admin app so SAMTWO can still save its own media. Nuisance measure, not real
// protection: applies to the img element only, so click/hover/keyboard/a11y stay intact.
export const mediaGuard = () => {
  const onAdmin = typeof window !== "undefined" && window.location.pathname.startsWith("/s2-console-9f4b");
  if (onAdmin) return { props: {}, style: {} };
  return {
    props: { onContextMenu: (e) => e.preventDefault(), draggable: false },
    style: { userSelect: "none", WebkitUserSelect: "none", WebkitTouchCallout: "none" },
  };
};

export const TileImage = ({ src, alt = "", className = "w-full h-full object-cover", extraTransition = "", testid, width, height, style = {} }) => {
  const [loaded, setLoaded] = useState(false);
  const g = mediaGuard();
  return (
    <>
      {!loaded && <span className="skeleton absolute inset-0" aria-hidden="true" />}
      <img
        src={src}
        alt={alt}
        loading="lazy"
        width={width}
        height={height}
        data-testid={testid}
        {...g.props}
        onLoad={() => setLoaded(true)}
        onError={() => setLoaded(true)}
        className={className}
        style={{ opacity: loaded ? 1 : 0, transition: `opacity 200ms ease${extraTransition ? ", " + extraTransition : ""}`, ...g.style, ...style }}
      />
    </>
  );
};

// Text placeholder that reserves the real line height and count so a card does not
// grow when copy arrives with the same fetch as its image. Same shimmer token.
export const SkeletonLines = ({ lines = 1, lineHeight = 14, gap = 6, widths = [], className = "" }) => (
  <div className={className} aria-hidden="true">
    {Array.from({ length: lines }).map((_, i) => (
      <span
        key={i}
        className="skeleton block"
        style={{ height: lineHeight, borderRadius: 6, width: widths[i] || (i === lines - 1 && lines > 1 ? "60%" : "100%"), marginTop: i === 0 ? 0 : gap }}
      />
    ))}
  </div>
);

// A few common composite skeletons.
export const SkeletonText = ({ lines = 3 }) => (
  <div className="space-y-2">
    {Array.from({ length: lines }).map((_, i) => (
      <Skeleton key={i} height={12} width={i === lines - 1 ? "60%" : "100%"} />
    ))}
  </div>
);

export const SkeletonCard = () => (
  <div className="glass-card p-6 space-y-4">
    <div className="flex items-center gap-3">
      <Skeleton width={40} height={40} borderRadius={9999} />
      <Skeleton width={120} height={12} />
    </div>
    <SkeletonText lines={3} />
  </div>
);
